// navigator.audioSession (WebKit's Audio Session API, Safari 16.4+): what kind of audio the page
// plays, which decides the iOS audio category. Elsewhere the API is missing and all of this does
// nothing, so PCs are unaffected.
//
// Two places set it:
// - volume.ts routes our levelled recordings through Web Audio and sets 'playback', so they play
//   with the ring/silent switch on like an <audio> element does (iOS 17+).
// - Recording (pronunciation/useRecorder.ts, showaudio/capture.ts) needs 'play-and-record': in the
//   'playback' category iOS gives no microphone input, so a take recorded after a levelled playback
//   came out silent or failed. The recorder holds 'play-and-record' while the microphone is open and
//   gives back the type it found ('playback' or 'auto') when the last hold is released.

type SessionType = 'auto' | 'playback' | 'transient' | 'transient-solo' | 'ambient' | 'play-and-record';
interface AudioSessionLike {
  type: SessionType;
}

function session(): AudioSessionLike | null {
  if (typeof navigator === 'undefined') return null;
  return (navigator as Navigator & { audioSession?: AudioSessionLike }).audioSession ?? null;
}

let holds = 0;
/** The type to go back to once the microphone is released. */
let restore: SessionType = 'auto';

function setType(s: AudioSessionLike, t: SessionType): void {
  try {
    if (s.type !== t) s.type = t;
  } catch {
    /* an unknown type on an older WebKit: leave it */
  }
}

/** Web Audio playback (volume.ts): 'playback', unless the page already chose a type. */
export function preferPlayback(): void {
  const s = session();
  if (!s) return;
  if (holds > 0) {
    // recording right now: play-and-record stays; playback once the microphone is released
    if (restore === 'auto') restore = 'playback';
    return;
  }
  if (s.type === 'auto') setType(s, 'playback');
}

/**
 * Before getUserMedia: the session can record. Returns the release (call it once, when the
 * microphone's tracks are stopped or opening it failed); further calls do nothing.
 */
export function holdRecordSession(): () => void {
  const s = session();
  if (!s) return () => undefined;
  if (holds === 0) {
    restore = s.type === 'play-and-record' ? 'auto' : s.type;
    setType(s, 'play-and-record');
  }
  holds++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds = Math.max(0, holds - 1);
    if (holds === 0) setType(s, restore);
  };
}

/** Tests: forget the holds. */
export function __resetAudioSession(): void {
  holds = 0;
  restore = 'auto';
}
